/*
 * hash-stream.js — incremental (streaming) hashes.  window.HashStream
 *
 * Why this exists — two reasons, both measured 2026-08-24:
 *
 *  1. `crypto.subtle.digest` HAS NO MD5. It throws NotSupportedError in every
 *     engine. /checksum-calculator called it anyway, with MD5 checked by
 *     DEFAULT, so its most-wanted algorithm printed «error» on every run.
 *     /hash-generator meanwhile carried a working pure-JS MD5 of its own. Two
 *     tools, two implementations, one of them broken — the divergence the
 *     shared-primitive rule exists to prevent.
 *
 *  2. `crypto.subtle.digest` is ONE-SHOT: it wants the whole file in one
 *     ArrayBuffer. /checksum-calculator read the file in 4 MB chunks and then
 *     concatenated them, i.e. held the file TWICE, which is why its uploader
 *     sat on the 100 MB image budget while the average rejected file was
 *     4.28 GB. Hashing is the most streamable operation there is; it had the
 *     tightest ceiling on the site.
 *
 * Every algorithm here is incremental: O(block) state, no accumulation. Feed
 * chunks with update(), read the digest at the end. Native subtle.digest stays
 * the fast path for small inputs (see hashFile) — this is the lane that makes
 * the big ones possible at all, not a replacement.
 *
 * Correctness is asserted against Web Crypto (SHA family) and RFC test vectors
 * (MD5, CRC-32) in tests/playwright/hash-stream-vectors.mjs. Do not edit an
 * algorithm without re-running it.
 */
(function (root) {
    'use strict';

    function hex(bytes) {
        var s = '';
        for (var i = 0; i < bytes.length; i++) s += (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
        return s;
    }

    /* ── CRC-32 (IEEE 802.3, the zip/png polynomial) ─────────────────────── */
    var CRC_T = (function () {
        var t = new Uint32Array(256);
        for (var i = 0; i < 256; i++) {
            var c = i;
            for (var k = 0; k < 8; k++) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
            t[i] = c >>> 0;
        }
        return t;
    })();
    function Crc32() { this.c = 0xffffffff; }
    Crc32.prototype.update = function (chunk) {
        var c = this.c;
        for (var i = 0; i < chunk.length; i++) c = CRC_T[(c ^ chunk[i]) & 0xff] ^ (c >>> 8);
        this.c = c >>> 0;
    };
    Crc32.prototype.digest = function () {
        var v = (this.c ^ 0xffffffff) >>> 0, out = new Uint8Array(4);
        out[0] = (v >>> 24) & 0xff; out[1] = (v >>> 16) & 0xff; out[2] = (v >>> 8) & 0xff; out[3] = v & 0xff;
        return out;
    };

    /* ── shared 64-byte block plumbing ───────────────────────────────────── */
    function Blocked(blockSize, compress) {
        this.bs = blockSize;
        this.buf = new Uint8Array(blockSize);
        this.len = 0;
        this.total = 0;
        this._c = compress;
    }
    Blocked.prototype.update = function (chunk) {
        this.total += chunk.length;
        var i = 0, bs = this.bs;
        if (this.len > 0) {
            var need = bs - this.len;
            if (chunk.length < need) { this.buf.set(chunk, this.len); this.len += chunk.length; return; }
            this.buf.set(chunk.subarray(0, need), this.len);
            this._c(this.buf);
            this.len = 0; i = need;
        }
        while (i + bs <= chunk.length) { this._c(chunk.subarray(i, i + bs)); i += bs; }
        if (i < chunk.length) { this.len = chunk.length - i; this.buf.set(chunk.subarray(i), 0); }
    };
    /* Padding for the whole family: 0x80, zeros, then the message length.
       `le` picks MD5's little-endian length; SHA writes big-endian. `lenBytes`
       is 8 for the 64-byte block algorithms and 16 for SHA-512's 128-byte one. */
    Blocked.prototype._pad = function (le, lenBytes) {
        var bs = this.bs, total = this.total;
        var rest = (total + 1 + lenBytes) % bs;
        var zeros = rest === 0 ? 0 : bs - rest;
        var pad = new Uint8Array(1 + zeros + lenBytes);
        pad[0] = 0x80;
        // Bit length as a 64-bit value. total is a JS number (< 2^53), so the
        // top word never needs more than 21 bits — no BigInt required.
        var lo = (total % 0x20000000) * 8;           // low 32 bits of total*8
        var hi = Math.floor(total / 0x20000000);     // high bits of total*8
        while (lo >= 0x100000000) { lo -= 0x100000000; hi += 1; }
        var at = 1 + zeros;
        if (le) {
            pad[at] = lo & 0xff; pad[at + 1] = (lo >>> 8) & 0xff;
            pad[at + 2] = (lo >>> 16) & 0xff; pad[at + 3] = (lo >>> 24) & 0xff;
            pad[at + 4] = hi & 0xff; pad[at + 5] = (hi >>> 8) & 0xff;
            pad[at + 6] = (hi >>> 16) & 0xff; pad[at + 7] = (hi >>> 24) & 0xff;
        } else {
            var end = at + lenBytes;
            pad[end - 1] = lo & 0xff; pad[end - 2] = (lo >>> 8) & 0xff;
            pad[end - 3] = (lo >>> 16) & 0xff; pad[end - 4] = (lo >>> 24) & 0xff;
            pad[end - 5] = hi & 0xff; pad[end - 6] = (hi >>> 8) & 0xff;
            pad[end - 7] = (hi >>> 16) & 0xff; pad[end - 8] = (hi >>> 24) & 0xff;
        }
        this.update(pad);
    };

    /* ── MD5 (RFC 1321) — lifted from /hash-generator, which is where the only
         working implementation on the site lived. ─────────────────────────── */
    var MD5_S = [7,12,17,22,7,12,17,22,7,12,17,22,7,12,17,22,
                 5,9,14,20,5,9,14,20,5,9,14,20,5,9,14,20,
                 4,11,16,23,4,11,16,23,4,11,16,23,4,11,16,23,
                 6,10,15,21,6,10,15,21,6,10,15,21,6,10,15,21];
    var MD5_K = (function () {
        var K = new Uint32Array(64);
        for (var i = 0; i < 64; i++) K[i] = Math.floor(Math.abs(Math.sin(i + 1)) * 0x100000000) >>> 0;
        return K;
    })();
    function Md5() {
        var self = this;
        this.h = new Uint32Array([0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476]);
        this.M = new Uint32Array(16);
        this.b = new Blocked(64, function (blk) { self._c(blk); });
    }
    function rotl(x, n) { return ((x << n) | (x >>> (32 - n))) >>> 0; }
    Md5.prototype._c = function (block) {
        var M = this.M, h = this.h, j;
        for (j = 0; j < 16; j++) M[j] = block[j*4] | (block[j*4+1] << 8) | (block[j*4+2] << 16) | (block[j*4+3] << 24);
        var A = h[0], B = h[1], C = h[2], D = h[3];
        for (var i = 0; i < 64; i++) {
            var F, g;
            if (i < 16) { F = (B & C) | ((~B) & D); g = i; }
            else if (i < 32) { F = (D & B) | ((~D) & C); g = (5*i + 1) & 15; }
            else if (i < 48) { F = B ^ C ^ D; g = (3*i + 5) & 15; }
            else { F = C ^ (B | (~D)); g = (7*i) & 15; }
            F = (F + A + MD5_K[i] + M[g]) >>> 0;
            A = D; D = C; C = B; B = (B + rotl(F, MD5_S[i])) >>> 0;
        }
        h[0] = (h[0] + A) >>> 0; h[1] = (h[1] + B) >>> 0; h[2] = (h[2] + C) >>> 0; h[3] = (h[3] + D) >>> 0;
    };
    Md5.prototype.update = function (c) { this.b.update(c); };
    Md5.prototype.digest = function () {
        this.b._pad(true, 8);
        var out = new Uint8Array(16), dv = new DataView(out.buffer);
        for (var i = 0; i < 4; i++) dv.setUint32(i * 4, this.h[i], true);
        return out;
    };

    /* ── SHA-1 (FIPS 180-4) ──────────────────────────────────────────────── */
    function Sha1() {
        var self = this;
        this.h = new Uint32Array([0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476, 0xc3d2e1f0]);
        this.W = new Uint32Array(80);
        this.b = new Blocked(64, function (blk) { self._c(blk); });
    }
    Sha1.prototype._c = function (block) {
        var W = this.W, h = this.h, i;
        for (i = 0; i < 16; i++) W[i] = (block[i*4] << 24) | (block[i*4+1] << 16) | (block[i*4+2] << 8) | block[i*4+3];
        for (i = 16; i < 80; i++) W[i] = rotl(W[i-3] ^ W[i-8] ^ W[i-14] ^ W[i-16], 1);
        var a = h[0], b = h[1], c = h[2], d = h[3], e = h[4];
        for (i = 0; i < 80; i++) {
            var f, k;
            if (i < 20) { f = (b & c) | ((~b) & d); k = 0x5a827999; }
            else if (i < 40) { f = b ^ c ^ d; k = 0x6ed9eba1; }
            else if (i < 60) { f = (b & c) | (b & d) | (c & d); k = 0x8f1bbcdc; }
            else { f = b ^ c ^ d; k = 0xca62c1d6; }
            var t = (rotl(a, 5) + f + e + k + W[i]) >>> 0;
            e = d; d = c; c = rotl(b, 30); b = a; a = t;
        }
        h[0] = (h[0] + a) >>> 0; h[1] = (h[1] + b) >>> 0; h[2] = (h[2] + c) >>> 0;
        h[3] = (h[3] + d) >>> 0; h[4] = (h[4] + e) >>> 0;
    };
    Sha1.prototype.update = function (c) { this.b.update(c); };
    Sha1.prototype.digest = function () {
        this.b._pad(false, 8);
        var out = new Uint8Array(20), dv = new DataView(out.buffer);
        for (var i = 0; i < 5; i++) dv.setUint32(i * 4, this.h[i], false);
        return out;
    };

    /* ── SHA-256 (FIPS 180-4) ────────────────────────────────────────────── */
    var K256 = new Uint32Array([
        0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
        0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
        0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
        0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
        0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
        0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
        0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
        0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2]);
    function rotr(x, n) { return ((x >>> n) | (x << (32 - n))) >>> 0; }
    function Sha256() {
        var self = this;
        this.h = new Uint32Array([0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19]);
        this.W = new Uint32Array(64);
        this.b = new Blocked(64, function (blk) { self._c(blk); });
    }
    Sha256.prototype._c = function (block) {
        var W = this.W, h = this.h, i;
        for (i = 0; i < 16; i++) W[i] = (block[i*4] << 24) | (block[i*4+1] << 16) | (block[i*4+2] << 8) | block[i*4+3];
        for (i = 16; i < 64; i++) {
            var s0 = rotr(W[i-15], 7) ^ rotr(W[i-15], 18) ^ (W[i-15] >>> 3);
            var s1 = rotr(W[i-2], 17) ^ rotr(W[i-2], 19) ^ (W[i-2] >>> 10);
            W[i] = (W[i-16] + s0 + W[i-7] + s1) >>> 0;
        }
        var a=h[0],b=h[1],c=h[2],d=h[3],e=h[4],f=h[5],g=h[6],hh=h[7];
        for (i = 0; i < 64; i++) {
            var S1 = rotr(e,6) ^ rotr(e,11) ^ rotr(e,25);
            var ch = (e & f) ^ ((~e) & g);
            var t1 = (hh + S1 + ch + K256[i] + W[i]) >>> 0;
            var S0 = rotr(a,2) ^ rotr(a,13) ^ rotr(a,22);
            var maj = (a & b) ^ (a & c) ^ (b & c);
            var t2 = (S0 + maj) >>> 0;
            hh=g; g=f; f=e; e=(d+t1)>>>0; d=c; c=b; b=a; a=(t1+t2)>>>0;
        }
        h[0]=(h[0]+a)>>>0; h[1]=(h[1]+b)>>>0; h[2]=(h[2]+c)>>>0; h[3]=(h[3]+d)>>>0;
        h[4]=(h[4]+e)>>>0; h[5]=(h[5]+f)>>>0; h[6]=(h[6]+g)>>>0; h[7]=(h[7]+hh)>>>0;
    };
    Sha256.prototype.update = function (c) { this.b.update(c); };
    Sha256.prototype.digest = function () {
        this.b._pad(false, 8);
        var out = new Uint8Array(32), dv = new DataView(out.buffer);
        for (var i = 0; i < 8; i++) dv.setUint32(i * 4, this.h[i], false);
        return out;
    };

    /* ── SHA-512 / SHA-384 (FIPS 180-4), 64-bit words as hi/lo pairs ─────── */
    var K512 = [
        '428a2f98d728ae22','7137449123ef65cd','b5c0fbcfec4d3b2f','e9b5dba58189dbbc',
        '3956c25bf348b538','59f111f1b605d019','923f82a4af194f9b','ab1c5ed5da6d8118',
        'd807aa98a3030242','12835b0145706fbe','243185be4ee4b28c','550c7dc3d5ffb4e2',
        '72be5d74f27b896f','80deb1fe3b1696b1','9bdc06a725c71235','c19bf174cf692694',
        'e49b69c19ef14ad2','efbe4786384f25e3','0fc19dc68b8cd5b5','240ca1cc77ac9c65',
        '2de92c6f592b0275','4a7484aa6ea6e483','5cb0a9dcbd41fbd4','76f988da831153b5',
        '983e5152ee66dfab','a831c66d2db43210','b00327c898fb213f','bf597fc7beef0ee4',
        'c6e00bf33da88fc2','d5a79147930aa725','06ca6351e003826f','142929670a0e6e70',
        '27b70a8546d22ffc','2e1b21385c26c926','4d2c6dfc5ac42aed','53380d139d95b3df',
        '650a73548baf63de','766a0abb3c77b2a8','81c2c92e47edaee6','92722c851482353b',
        'a2bfe8a14cf10364','a81a664bbc423001','c24b8b70d0f89791','c76c51a30654be30',
        'd192e819d6ef5218','d69906245565a910','f40e35855771202a','106aa07032bbd1b8',
        '19a4c116b8d2d0c8','1e376c085141ab53','2748774cdf8eeb99','34b0bcb5e19b48a8',
        '391c0cb3c5c95a63','4ed8aa4ae3418acb','5b9cca4f7763e373','682e6ff3d6b2b8a3',
        '748f82ee5defb2fc','78a5636f43172f60','84c87814a1f0ab72','8cc702081a6439ec',
        '90befffa23631e28','a4506cebde82bde9','bef9a3f7b2c67915','c67178f2e372532b',
        'ca273eceea26619c','d186b8c721c0c207','eada7dd6cde0eb1e','f57d4f7fee6ed178',
        '06f067aa72176fba','0a637dc5a2c898a6','113f9804bef90dae','1b710b35131c471b',
        '28db77f523047d84','32caab7b40c72493','3c9ebe0a15c9bebc','431d67c49c100d4c',
        '4cc5d4becb3e42b6','597f299cfc657e2a','5fcb6fab3ad6faec','6c44198c4a475817'
    ];
    var K512H = new Uint32Array(80), K512L = new Uint32Array(80);
    for (var _k = 0; _k < 80; _k++) {
        K512H[_k] = parseInt(K512[_k].slice(0, 8), 16) >>> 0;
        K512L[_k] = parseInt(K512[_k].slice(8), 16) >>> 0;
    }
    var IV512 = ['6a09e667f3bcc908','bb67ae8584caa73b','3c6ef372fe94f82b','a54ff53a5f1d36f1',
                 '510e527fade682d1','9b05688c2b3e6c1f','1f83d9abfb41bd6b','5be0cd19137e2179'];
    var IV384 = ['cbbb9d5dc1059ed8','629a292a367cd507','9159015a3070dd17','152fecd8f70e5939',
                 '67332667ffc00b31','8eb44a8768581511','db0c2e0d64f98fa7','47b5481dbefa4fa4'];
    function Sha512(iv, outBytes) {
        var self = this;
        this.hh = new Uint32Array(8); this.hl = new Uint32Array(8);
        for (var i = 0; i < 8; i++) {
            this.hh[i] = parseInt(iv[i].slice(0, 8), 16) >>> 0;
            this.hl[i] = parseInt(iv[i].slice(8), 16) >>> 0;
        }
        this.outBytes = outBytes;
        this.WH = new Uint32Array(80); this.WL = new Uint32Array(80);
        this.b = new Blocked(128, function (blk) { self._c(blk); });
    }
    Sha512.prototype._c = function (block) {
        var WH = this.WH, WL = this.WL, i;
        for (i = 0; i < 16; i++) {
            var o = i * 8;
            WH[i] = ((block[o] << 24) | (block[o+1] << 16) | (block[o+2] << 8) | block[o+3]) >>> 0;
            WL[i] = ((block[o+4] << 24) | (block[o+5] << 16) | (block[o+6] << 8) | block[o+7]) >>> 0;
        }
        for (i = 16; i < 80; i++) {
            // s0 = ROTR1 ^ ROTR8 ^ SHR7  of W[i-15]
            var xh = WH[i-15], xl = WL[i-15];
            var s0h = ((xh >>> 1) | (xl << 31)) ^ ((xh >>> 8) | (xl << 24)) ^ (xh >>> 7);
            var s0l = ((xl >>> 1) | (xh << 31)) ^ ((xl >>> 8) | (xh << 24)) ^ ((xl >>> 7) | (xh << 25));
            // s1 = ROTR19 ^ ROTR61 ^ SHR6  of W[i-2]
            var yh = WH[i-2], yl = WL[i-2];
            var s1h = ((yh >>> 19) | (yl << 13)) ^ ((yl >>> 29) | (yh << 3)) ^ (yh >>> 6);
            var s1l = ((yl >>> 19) | (yh << 13)) ^ ((yh >>> 29) | (yl << 3)) ^ ((yl >>> 6) | (yh << 26));
            // W[i] = W[i-16] + s0 + W[i-7] + s1  (64-bit add via lo carry)
            var l = (WL[i-16] >>> 0) + (s0l >>> 0);
            var h = (WH[i-16] >>> 0) + (s0h >>> 0) + (l > 0xffffffff ? 1 : 0);
            l = (l >>> 0) + (WL[i-7] >>> 0);
            h = (h >>> 0) + (WH[i-7] >>> 0) + (l > 0xffffffff ? 1 : 0);
            l = (l >>> 0) + (s1l >>> 0);
            h = (h >>> 0) + (s1h >>> 0) + (l > 0xffffffff ? 1 : 0);
            WH[i] = h >>> 0; WL[i] = l >>> 0;
        }
        var ah=this.hh[0],al=this.hl[0], bh=this.hh[1],bl=this.hl[1],
            ch=this.hh[2],cl=this.hl[2], dh=this.hh[3],dl=this.hl[3],
            eh=this.hh[4],el=this.hl[4], fh=this.hh[5],fl=this.hl[5],
            gh=this.hh[6],gl=this.hl[6], hh_=this.hh[7],hl_=this.hl[7];
        for (i = 0; i < 80; i++) {
            // S1 = ROTR14 ^ ROTR18 ^ ROTR41 of e
            var S1h = ((eh >>> 14) | (el << 18)) ^ ((eh >>> 18) | (el << 14)) ^ ((el >>> 9) | (eh << 23));
            var S1l = ((el >>> 14) | (eh << 18)) ^ ((el >>> 18) | (eh << 14)) ^ ((eh >>> 9) | (el << 23));
            var chh = (eh & fh) ^ ((~eh) & gh);
            var chl = (el & fl) ^ ((~el) & gl);
            // t1 = h + S1 + ch + K[i] + W[i]
            var tl = (hl_ >>> 0) + (S1l >>> 0);
            var th = (hh_ >>> 0) + (S1h >>> 0) + (tl > 0xffffffff ? 1 : 0);
            tl = (tl >>> 0) + (chl >>> 0); th = (th >>> 0) + (chh >>> 0) + (tl > 0xffffffff ? 1 : 0);
            tl = (tl >>> 0) + (K512L[i] >>> 0); th = (th >>> 0) + (K512H[i] >>> 0) + (tl > 0xffffffff ? 1 : 0);
            tl = (tl >>> 0) + (WL[i] >>> 0); th = (th >>> 0) + (WH[i] >>> 0) + (tl > 0xffffffff ? 1 : 0);
            var t1h = th >>> 0, t1l = tl >>> 0;
            // S0 = ROTR28 ^ ROTR34 ^ ROTR39 of a
            var S0h = ((ah >>> 28) | (al << 4)) ^ ((al >>> 2) | (ah << 30)) ^ ((al >>> 7) | (ah << 25));
            var S0l = ((al >>> 28) | (ah << 4)) ^ ((ah >>> 2) | (al << 30)) ^ ((ah >>> 7) | (al << 25));
            var majh = (ah & bh) ^ (ah & ch) ^ (bh & ch);
            var majl = (al & bl) ^ (al & cl) ^ (bl & cl);
            var t2l = (S0l >>> 0) + (majl >>> 0);
            var t2h = (S0h >>> 0) + (majh >>> 0) + (t2l > 0xffffffff ? 1 : 0);
            t2h = t2h >>> 0; t2l = t2l >>> 0;
            hh_ = gh; hl_ = gl; gh = fh; gl = fl; fh = eh; fl = el;
            var nl = (dl >>> 0) + (t1l >>> 0);
            var nh = (dh >>> 0) + (t1h >>> 0) + (nl > 0xffffffff ? 1 : 0);
            eh = nh >>> 0; el = nl >>> 0;
            dh = ch; dl = cl; ch = bh; cl = bl; bh = ah; bl = al;
            nl = (t1l >>> 0) + (t2l >>> 0);
            nh = (t1h >>> 0) + (t2h >>> 0) + (nl > 0xffffffff ? 1 : 0);
            ah = nh >>> 0; al = nl >>> 0;
        }
        var H = this.hh, L = this.hl;
        function add(i, xh, xl) {
            var l = (L[i] >>> 0) + (xl >>> 0);
            var h = (H[i] >>> 0) + (xh >>> 0) + (l > 0xffffffff ? 1 : 0);
            H[i] = h >>> 0; L[i] = l >>> 0;
        }
        add(0, ah, al); add(1, bh, bl); add(2, ch, cl); add(3, dh, dl);
        add(4, eh, el); add(5, fh, fl); add(6, gh, gl); add(7, hh_, hl_);
    };
    Sha512.prototype.update = function (c) { this.b.update(c); };
    Sha512.prototype.digest = function () {
        this.b._pad(false, 16);
        var out = new Uint8Array(64), dv = new DataView(out.buffer);
        for (var i = 0; i < 8; i++) { dv.setUint32(i * 8, this.hh[i], false); dv.setUint32(i * 8 + 4, this.hl[i], false); }
        return out.subarray(0, this.outBytes);
    };

    var FACTORY = {
        'MD5':     function () { return new Md5(); },
        'SHA-1':   function () { return new Sha1(); },
        'SHA-256': function () { return new Sha256(); },
        'SHA-384': function () { return new Sha512(IV384, 48); },
        'SHA-512': function () { return new Sha512(IV512, 64); },
        'CRC-32':  function () { return new Crc32(); }
    };
    var ALGOS = ['MD5', 'SHA-1', 'SHA-256', 'SHA-384', 'SHA-512', 'CRC-32'];

    function create(algo) {
        var f = FACTORY[algo];
        if (!f) throw new Error('HashStream: unknown algorithm ' + algo);
        return f();
    }

    /* HMAC (RFC 2104) over the same incremental hashers, so a keyed digest of a
       multi-GB file costs no more memory than an unkeyed one. Web Crypto's
       HMAC is one-shot for the same reason digest() is, and it has no MD5 —
       and MD5 is exactly what a keyed checksum tool is usually asked for. */
    var HMAC_BLOCK = { 'MD5': 64, 'SHA-1': 64, 'SHA-256': 64, 'SHA-384': 128, 'SHA-512': 128 };
    function createHmac(algo, keyBytes) {
        var bs = HMAC_BLOCK[algo];
        if (!bs) throw new Error('HashStream: no HMAC for ' + algo);
        var key = keyBytes;
        if (key.length > bs) { var kh = create(algo); kh.update(key); key = kh.digest(); }
        var k = new Uint8Array(bs);
        k.set(key);
        var ipad = new Uint8Array(bs), opad = new Uint8Array(bs);
        for (var i = 0; i < bs; i++) { ipad[i] = k[i] ^ 0x36; opad[i] = k[i] ^ 0x5c; }
        var inner = create(algo);
        inner.update(ipad);
        return {
            update: function (c) { inner.update(c); },
            digest: function () {
                var id = inner.digest();
                var outer = create(algo);
                outer.update(opad); outer.update(id);
                return outer.digest();
            }
        };
    }

    /* Native subtle.digest is 5-20x faster than JS, so small inputs still use it.
       The threshold is about MEMORY, not speed: subtle needs the whole input in
       one buffer, so it is only allowed while that buffer is unremarkable. */
    var NATIVE_MAX_BYTES = 64 * 1024 * 1024;
    var NATIVE_OK = { 'SHA-1': 1, 'SHA-256': 1, 'SHA-384': 1, 'SHA-512': 1 };

    /* Read a File/Blob once and feed every requested hash from the same chunk.
       Returns { algo: hexDigest }. Never holds more than one chunk, so the
       input size is bounded by the disk, not by memory.
       onProgress(fraction 0..1) is called per chunk; returning FALSE from it
       aborts the read and throws 'HashStream: cancelled'. Cancelling between
       FILES is not enough when one file is 4 GB — the way out has to work
       inside the file too. */
    async function hashFile(file, algos, onProgress, hmacKeyBytes) {
        algos = (algos || []).filter(function (a) { return !!FACTORY[a]; });
        if (hmacKeyBytes) algos = algos.filter(function (a) { return !!HMAC_BLOCK[a]; });
        if (!algos.length) return {};
        var out = {};
        var mk = function (a) { return hmacKeyBytes ? createHmac(a, hmacKeyBytes) : create(a); };

        if (!hmacKeyBytes && file.size <= NATIVE_MAX_BYTES && typeof crypto !== 'undefined' && crypto.subtle) {
            var buf = new Uint8Array(await file.arrayBuffer());
            for (var n = 0; n < algos.length; n++) {
                var a = algos[n];
                if (NATIVE_OK[a]) {
                    try { out[a] = hex(new Uint8Array(await crypto.subtle.digest(a, buf))); continue; } catch (_) { /* fall through to JS */ }
                }
                var h1 = mk(a); h1.update(buf); out[a] = hex(h1.digest());
            }
            if (onProgress) onProgress(1);
            return out;
        }

        var hs = algos.map(function (a) { return { a: a, h: mk(a) }; });
        var read = 0, size = file.size || 0;
        if (typeof file.stream === 'function') {
            var reader = file.stream().getReader();
            for (;;) {
                var r = await reader.read();
                if (r.done) break;
                var v = r.value;
                for (var i = 0; i < hs.length; i++) hs[i].h.update(v);
                read += v.length;
                if (onProgress && size && onProgress(read / size) === false) {
                    try { await reader.cancel(); } catch (_) {}
                    throw new Error('HashStream: cancelled');
                }
            }
        } else {
            // Blob.stream() is absent on older WebKit — slice-loop fallback.
            var CH = 4 * 1024 * 1024;
            for (var off = 0; off < size; off += CH) {
                var part = new Uint8Array(await file.slice(off, Math.min(off + CH, size)).arrayBuffer());
                for (var j = 0; j < hs.length; j++) hs[j].h.update(part);
                read += part.length;
                if (onProgress && onProgress(read / size) === false) throw new Error('HashStream: cancelled');
            }
        }
        for (var k = 0; k < hs.length; k++) out[hs[k].a] = hex(hs[k].h.digest());
        return out;
    }

    /* Digests come back as hex because that is what every caller displays.
       A caller that needs the bytes (base64 output, byte comparison) converts
       back rather than the API growing a mode flag. */
    function unhex(h) {
        var out = new Uint8Array(h.length >> 1);
        for (var i = 0; i < out.length; i++) out[i] = parseInt(h.substr(i * 2, 2), 16);
        return out;
    }

    root.HashStream = {
        create: create,
        createHmac: createHmac,
        unhex: unhex,
        hashFile: hashFile,
        hex: hex,
        ALGOS: ALGOS,
        NATIVE_MAX_BYTES: NATIVE_MAX_BYTES
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = root.HashStream;
})(typeof self !== 'undefined' ? self : this);
